// Orchestration + state machine. Ties the panel UI to the recorder session and the transport.
// Flow: idle -> prepping -> starting -> recording (<=20m or <=size cap) -> uploading -> done,
// with a guarded discard. No pause: audio must be continuous so transcript timestamps stay
// aligned with the structured timeline (hard invariant the ingestor depends on).

import { startSession } from './recorder/index.js';
import { createAudioRecorder } from './recorder/audio.js';
import { buildPayload, buildMultipart } from './packager.js';
import { upload } from './transport.js';

export const State = {
  IDLE: 'idle',
  PREPPING: 'prepping',
  STARTING: 'starting', // async gap between "Start" click and capture running
  RECORDING: 'recording',
  DISCARD: 'discard_confirm',
  UPLOADING: 'uploading',
  DONE: 'done',
  ERROR: 'error',
};

export class Controller {
  constructor(cfg, ui) {
    this.cfg = cfg;
    this.ui = ui; // { panel, launcher }
    this.state = State.IDLE;
    this.audio = null;
    this.session = null;
    this.timer = null;
    this.startTime = 0;
    this.gen = 0; // generation token: bumped on reset so stale async continuations bail out
    this.lastForm = null;
    this._wire();
  }

  _wire() {
    const p = this.ui.panel;
    p.on('cancel', () => this._toIdle());
    p.on('start', () => this._start());
    p.on('discard', () => this._askDiscard());
    p.on('cancelDiscard', () => this._cancelDiscard());
    p.on('confirmDiscard', () => this._toIdle());
    p.on('send', () => this._send());
    p.on('retry', () => this._retry());
    p.on('close', () => this._toIdle());

    // ESC cancels from the prepping panel only (never discards a live recording).
    this._onKey = (e) => {
      if (e.key === 'Escape' && this.state === State.PREPPING) this._toIdle();
    };
    document.addEventListener('keydown', this._onKey);

    // Warn before tab close / full navigation while a recording or upload would be lost.
    this._onBeforeUnload = (e) => {
      if ([State.RECORDING, State.DISCARD, State.STARTING, State.UPLOADING].includes(this.state)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', this._onBeforeUnload);
  }

  async open() {
    if (this.state !== State.IDLE) return;
    this.state = State.PREPPING;
    const gen = this.gen;
    this.ui.launcher.hide();
    let audio = null;
    try {
      audio = await createAudioRecorder();
    } catch (e) {
      console.warn('[ff] microphone unavailable/denied', e);
    }
    if (gen !== this.gen || this.state !== State.PREPPING) {
      // reset while we awaited the permission prompt — release the mic and bail
      if (audio) audio.stop().catch(() => {});
      return;
    }
    this.audio = audio;
    if (audio) this.ui.panel.showPrepping();
    else this.ui.panel.showPreppingDenied();
  }

  async _start() {
    if (this.state !== State.PREPPING) return; // guards double-click Start
    this.state = State.STARTING;
    const gen = this.gen;
    this.ui.panel.setBusy(true); // no cancel/start clicks during the async gap

    let session = null;
    try {
      session = await startSession(this.cfg, this.audio);
    } catch (e) {
      console.warn('[ff] failed to start session', e);
    }

    if (gen !== this.gen || this.state !== State.STARTING) {
      // reset while starting — tear down whatever came up (patches + mic)
      if (session) session.stop().catch(() => {});
      return;
    }
    if (!session) return this._toIdle();

    this.session = session;
    this.audio = null; // owned by the session now
    this.state = State.RECORDING;
    this.ui.panel.showRecording(session.analyser);
    this.startTime = session.store.startedAt; // same zero point as timeline tMs
    this._tick();
    this.timer = setInterval(() => this._tick(), 200);
  }

  _elapsed() {
    return Date.now() - this.startTime;
  }

  _tick() {
    const capturing = this.state === State.RECORDING || this.state === State.DISCARD;
    if (!capturing) return;
    const elapsed = this._elapsed();
    this.ui.panel.setTime(elapsed, this.cfg.maxDurationMs);
    // Auto-send on either cap. Fires in DISCARD too — capture is still running there,
    // and un-confirmed discard shouldn't disable the duration/size guarantees.
    if (elapsed >= this.cfg.maxDurationMs) return this._send();
    if (this.session && this.session.store.approxBytes() >= this.cfg.maxPayloadBytes) {
      console.warn('[ff] payload size cap reached — auto-sending');
      return this._send();
    }
  }

  _askDiscard() {
    if (this.state !== State.RECORDING) return;
    this.state = State.DISCARD;
    this.ui.panel.showDiscardConfirm();
  }

  _cancelDiscard() {
    if (this.state !== State.DISCARD) return;
    this.state = State.RECORDING;
    this.ui.panel.renderControls();
  }

  async _send() {
    if (this.state !== State.RECORDING && this.state !== State.DISCARD) return;
    this._stopTimer();
    this.state = State.UPLOADING;
    this.ui.panel.showUploading();

    let result;
    try {
      const session = this.session;
      this.session = null;
      const { store, audioBlob } = await session.stop();
      const payload = buildPayload(this.cfg, store, audioBlob);

      if (this.cfg.dryRun) {
        console.log('[ff] DRY RUN — payload that would be POSTed to', this.cfg.uploadUrl || '(no endpoint)', payload);
        if (audioBlob) console.log('[ff] DRY RUN — audio blob', audioBlob.type, audioBlob.size + ' bytes');
        return this._done();
      }

      this.lastForm = await buildMultipart(payload, audioBlob);
      result = await upload(this.cfg, this.lastForm, {
        onAttempt: (n, max) => this.ui.panel.setUploadAttempt(n, max),
      });
    } catch (e) {
      result = { ok: false, error: String(e) };
    }

    if (this.state !== State.UPLOADING) return; // reset while uploading
    if (result.ok) this._done();
    else this._fail(result);
  }

  async _retry() {
    if (this.state !== State.ERROR || !this.lastForm) return this._toIdle();
    this.state = State.UPLOADING;
    this.ui.panel.showUploading();
    const result = await upload(this.cfg, this.lastForm, {
      onAttempt: (n, max) => this.ui.panel.setUploadAttempt(n, max),
    });
    if (this.state !== State.UPLOADING) return;
    if (result.ok) this._done();
    else this._fail(result);
  }

  _fail(result) {
    this.state = State.ERROR;
    if (result.fatal) this.lastForm = null; // a 4xx won't succeed on retry — don't pretend
    const msg = result.error || (result.status ? `Server responded ${result.status}.` : 'Upload failed.');
    this.ui.panel.showError(msg, { retryable: !!this.lastForm });
  }

  _done() {
    this.state = State.DONE;
    this.lastForm = null;
    this.ui.panel.showDone({ dryRun: this.cfg.dryRun });
    setTimeout(() => { if (this.state === State.DONE) this._toIdle(); }, this.cfg.dryRun ? 3500 : 2200);
  }

  _toIdle() {
    this.gen++; // invalidate any in-flight open()/_start() continuation
    this._stopTimer();
    if (this.session) { this.session.stop().catch(() => {}); this.session = null; }
    if (this.audio) { this.audio.stop().catch(() => {}); this.audio = null; }
    this.lastForm = null;
    this.state = State.IDLE;
    this.ui.panel.hide();
    this.ui.launcher.show();
  }

  _stopTimer() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }
}
